import { useState, useEffect, useRef, useCallback } from 'react';
import axios from 'axios';
import { useAuth } from '../context/AuthContext';
import { Card, CardContent, CardHeader, CardTitle } from '../components/ui/card';
import { Button } from '../components/ui/button';
import { 
  DollarSign, 
  CreditCard, 
  Banknote, 
  AlertCircle,
  TrendingUp,
  Calendar,
  Sun,
  CalendarDays,
  Dumbbell,
  Apple,
  RefreshCw,
  ShoppingCart,
  Package
} from 'lucide-react';
import {
  BarChart,
  Bar,
  XAxis,
  YAxis,
  CartesianGrid,
  Tooltip,
  ResponsiveContainer,
  Legend
} from 'recharts';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const formatCurrency = (value) => {
  return new Intl.NumberFormat('es-ES', {
    style: 'currency',
    currency: 'EUR',
    minimumFractionDigits: 2
  }).format(value || 0);
};

// Category Breakdown Tooltip Component
const CategoryTooltip = ({ byCategory, title }) => {
  // Consistent color mapping for all categories
  const getCategoryConfig = (cat) => {
    const configs = {
      suscripciones: { icon: Dumbbell, color: '#A855F7', colorClass: 'purple', name: 'Suscripciones' },
      nutricion: { icon: Apple, color: '#22C55E', colorClass: 'green', name: 'Nutrición' },
      accesorios: { icon: ShoppingCart, color: '#3B82F6', colorClass: 'blue', name: 'Accesorios' },
      vestimenta: { icon: Package, color: '#F59E0B', colorClass: 'amber', name: 'Vestimenta' },
    };
    
    // For unknown categories, assign colors based on a hash of the name
    if (!configs[cat]) {
      const extraColors = [
        { color: '#EF4444', colorClass: 'red' },
        { color: '#06B6D4', colorClass: 'cyan' },
        { color: '#EC4899', colorClass: 'pink' },
        { color: '#84CC16', colorClass: 'lime' },
      ];
      const hash = cat.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
      const extraConfig = extraColors[hash % extraColors.length];
      return { 
        icon: Package, 
        ...extraConfig,
        name: cat.charAt(0).toUpperCase() + cat.slice(1) 
      };
    }
    return configs[cat];
  };
  
  const categories = byCategory ? Object.keys(byCategory).filter(cat => byCategory[cat] > 0) : [];
  
  return (
    <div className="absolute z-50 left-0 right-0 top-full mt-2 bg-zinc-800 border border-zinc-700 rounded-sm p-3 shadow-xl">
      <p className="text-xs text-zinc-400 mb-2 font-semibold">Desglose de {title}</p>
      <div className="space-y-2">
        {categories.map((cat) => {
          const config = getCategoryConfig(cat);
          const IconComponent = config.icon;
          return (
            <div key={cat} className="flex items-center justify-between">
              <div className="flex items-center gap-2">
                <IconComponent size={14} style={{ color: config.color }} />
                <span className="text-xs text-zinc-300">{config.name}</span>
              </div>
              <span className="text-xs font-mono" style={{ color: config.color }}>{formatCurrency(byCategory[cat] || 0)}</span>
            </div>
          );
        })}
        {categories.length === 0 && (
          <p className="text-xs text-zinc-500">Sin datos</p>
        )}
      </div>
    </div>
  );
};

// Helper function to get category color (used by chart)
const getCategoryColor = (cat) => {
  const colors = {
    suscripciones: '#A855F7',  // purple
    nutricion: '#22C55E',      // green
    accesorios: '#3B82F6',     // blue
    vestimenta: '#F59E0B',     // amber/orange
  };
  
  if (!colors[cat]) {
    const extraColors = ['#EF4444', '#06B6D4', '#EC4899', '#84CC16'];
    const hash = cat.split('').reduce((acc, char) => acc + char.charCodeAt(0), 0);
    return extraColors[hash % extraColors.length];
  }
  return colors[cat];
};

// Helper function to get category display name
const getCategoryName = (cat) => {
  const names = {
    suscripciones: 'Suscripciones',
    nutricion: 'Nutrición',
    accesorios: 'Accesorios',
    vestimenta: 'Vestimenta',
  };
  return names[cat] || cat.charAt(0).toUpperCase() + cat.slice(1);
};

// Stat Card Component
const StatCard = ({ title, value, icon: Icon, color, subValue, subLabel, tooltipId, byCategory, showBreakdown, activeTooltip, setActiveTooltip, refunds }) => (
  <div 
    className="bg-zinc-900/50 rounded-sm p-4 relative cursor-pointer hover:bg-zinc-900/80 transition-colors"
    onClick={() => showBreakdown && setActiveTooltip(activeTooltip === tooltipId ? null : tooltipId)}
    onMouseEnter={() => showBreakdown && setActiveTooltip(tooltipId)}
    onMouseLeave={() => showBreakdown && setActiveTooltip(null)}
  >
    <div className="flex items-start justify-between">
      <div className="min-w-0 flex-1">
        <p className="text-[10px] sm:text-xs text-zinc-500 uppercase tracking-wider font-semibold mb-1">
          {title}
          {showBreakdown && <span className="ml-1 text-[#FF3B30]">•</span>}
        </p>
        <p className="text-lg sm:text-xl font-mono font-bold text-white tracking-tight">{formatCurrency(value)}</p>
        {subValue !== undefined && (
          <p className="text-[10px] text-zinc-600 mt-1">{subLabel}: {subValue}</p>
        )}
        {refunds > 0 && (
          <p className="text-[10px] text-red-400 mt-1">(-{formatCurrency(refunds)} dev.)</p>
        )}
      </div>
      <div className={`p-2 rounded-sm ${color} flex-shrink-0 ml-2`}>
        <Icon size={16} className="text-white" />
      </div>
    </div>
    {showBreakdown && activeTooltip === tooltipId && byCategory && (
      <CategoryTooltip byCategory={byCategory} title={title} />
    )}
  </div>
);

const Dashboard = () => {
  const { user } = useAuth();
  const [stats, setStats] = useState(null);
  const [yearlyData, setYearlyData] = useState([]);
  const [loading, setLoading] = useState(true);
  const currentYear = new Date().getFullYear();
  const currentMonth = new Date().getMonth() + 1;
  const [activeTooltip, setActiveTooltip] = useState(null);
  const tooltipRef = useRef(null);

  // Check if user is admin
  const isAdmin = user?.role === 'admin' || user?.role === 'administrador';

  const months = [
    'Ene', 'Feb', 'Mar', 'Abr', 'May', 'Jun',
    'Jul', 'Ago', 'Sep', 'Oct', 'Nov', 'Dic'
  ];

  const monthsFull = [
    'Enero', 'Febrero', 'Marzo', 'Abril', 'Mayo', 'Junio',
    'Julio', 'Agosto', 'Septiembre', 'Octubre', 'Noviembre', 'Diciembre'
  ];

  // Close tooltip when clicking outside
  useEffect(() => {
    const handleClickOutside = (event) => {
      if (tooltipRef.current && !tooltipRef.current.contains(event.target)) {
        setActiveTooltip(null);
      }
    };
    document.addEventListener('mousedown', handleClickOutside);
    return () => document.removeEventListener('mousedown', handleClickOutside);
  }, []);

  useEffect(() => {
    fetchData();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const fetchData = async () => {
    setLoading(true);
    try {
      // Single optimized call to get all yearly data
      const response = await axios.get(`${API}/sales/stats/yearly`, {
        params: { year: currentYear }
      });
      
      const data = response.data;
      
      // Set yearly data for the chart
      setYearlyData(data.months || []);
      
      // Set stats with today's data and current month
      setStats({
        today: data.today,
        month: data.current_month,
        pendingDebt: data.pending_debt_total || 0
      });
    } catch (error) {
      console.error('Error fetching stats:', error);
    } finally {
      setLoading(false);
    }
  };

  if (loading) {
    return (
      <div className="flex items-center justify-center h-64">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-[#FF3B30]" />
      </div>
    );
  }

  const yearTotal = yearlyData.reduce((sum, m) => sum + m.total, 0);
  const currentMonthData = yearlyData[currentMonth - 1] || { efectivo: 0, tarjeta: 0, deuda: 0, total: 0, by_category: {} };
  
  // Get all unique categories from yearly data dynamically
  const allCategories = new Set();
  yearlyData.forEach(m => {
    if (m.by_category) {
      Object.keys(m.by_category).forEach(cat => allCategories.add(cat));
    }
  });
  const categoryList = Array.from(allCategories);
  
  // Calculate yearly totals by category - dynamic
  const yearlyByCategory = yearlyData.reduce((acc, m) => {
    categoryList.forEach(cat => {
      acc[cat] = (acc[cat] || 0) + (m.by_category?.[cat] || 0);
    });
    return acc;
  }, {});

  return (
    <div data-testid="dashboard-page" className="space-y-6">
      {/* Header */}
      <div className="flex items-center justify-between">
        <div className="flex items-center gap-3">
          <div>
            <h1 className="text-2xl sm:text-3xl font-black text-white">Dashboard</h1>
            <p className="text-zinc-500 text-sm mt-1">
              Bienvenido, <span className="text-[#FF3B30]">{user?.name}</span>
            </p>
          </div>
          <Button
            onClick={fetchData}
            variant="outline"
            size="sm"
            className="border-zinc-700 text-zinc-300 hover:bg-zinc-800"
            disabled={loading}
          >
            <RefreshCw size={16} className={loading ? 'animate-spin' : ''} />
          </Button>
        </div>
      </div>

      {/* ============ VENTAS DE HOY ============ */}
      <Card className="bg-[#0A0A0A] border-2 border-amber-600/30">
        <CardHeader className="pb-3 border-b border-zinc-800">
          <CardTitle className="flex items-center gap-3 text-amber-500">
            <div className="p-2 bg-amber-600/20 rounded-sm">
              <Sun size={20} />
            </div>
            <div>
              <span className="text-lg font-black uppercase tracking-wide">Ventas de Hoy</span>
              <p className="text-xs text-zinc-500 font-normal normal-case mt-0.5">
                {new Date().toLocaleDateString('es-ES', { weekday: 'long', day: 'numeric', month: 'long' })}
              </p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              title="Total Hoy"
              value={stats?.today?.total}
              icon={DollarSign}
              color="bg-amber-600"
              subValue={stats?.today?.count}
              subLabel="ventas"
              tooltipId="today-total"
              byCategory={stats?.today?.by_category}
              showBreakdown={true}
              activeTooltip={activeTooltip}
              setActiveTooltip={setActiveTooltip}
              refunds={stats?.today?.devoluciones}
            />
            <StatCard
              title="Efectivo"
              value={stats?.today?.ventas_efectivo || stats?.today?.efectivo}
              icon={Banknote}
              color="bg-emerald-600"
              refunds={stats?.today?.dev_efectivo}
            />
            <StatCard
              title="Tarjeta"
              value={stats?.today?.ventas_tarjeta || stats?.today?.tarjeta}
              icon={CreditCard}
              color="bg-blue-600"
              refunds={stats?.today?.dev_tarjeta}
            />
            <StatCard
              title="Deuda"
              value={stats?.today?.deuda}
              icon={AlertCircle}
              color="bg-red-600"
            />
          </div>
        </CardContent>
      </Card>

      {/* ============ RESUMEN MENSUAL (Admin + Vendedor) ============ */}
      <Card className="bg-[#0A0A0A] border-2 border-blue-600/30">
        <CardHeader className="pb-3 border-b border-zinc-800">
          <CardTitle className="flex items-center gap-3 text-blue-500">
            <div className="p-2 bg-blue-600/20 rounded-sm">
              <Calendar size={20} />
            </div>
            <div>
              <span className="text-lg font-black uppercase tracking-wide">Resumen del Mes</span>
              <p className="text-xs text-zinc-500 font-normal normal-case mt-0.5">
                {monthsFull[currentMonth - 1]} {currentYear}
              </p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-2 lg:grid-cols-4 gap-3">
            <StatCard
              title="Total Mensual"
              value={currentMonthData.total}
              icon={TrendingUp}
              color="bg-blue-600"
              subValue={stats?.month?.count}
              subLabel="transacciones"
              tooltipId="month-total"
              byCategory={currentMonthData.by_category}
              showBreakdown={true}
              activeTooltip={activeTooltip}
              setActiveTooltip={setActiveTooltip}
              refunds={currentMonthData.devoluciones}
            />
            <StatCard
              title="Efectivo"
              value={currentMonthData.efectivo}
              icon={Banknote}
              color="bg-emerald-600"
            />
            <StatCard
              title="Tarjeta"
              value={currentMonthData.tarjeta}
              icon={CreditCard}
              color="bg-sky-600"
            />
            <StatCard
              title="Deuda"
              value={currentMonthData.deuda}
              icon={AlertCircle}
              color="bg-red-600"
            />
          </div>
        </CardContent>
      </Card>

      {/* ============ RESUMEN ANUAL (Solo Admin) ============ */}
      {isAdmin && (
      <Card className="bg-[#0A0A0A] border-2 border-purple-600/30">
        <CardHeader className="pb-3 border-b border-zinc-800">
          <CardTitle className="flex items-center gap-3 text-purple-500">
            <div className="p-2 bg-purple-600/20 rounded-sm">
              <CalendarDays size={20} />
            </div>
            <div>
              <span className="text-lg font-black uppercase tracking-wide">Resumen Anual</span>
              <p className="text-xs text-zinc-500 font-normal normal-case mt-0.5">
                Año {currentYear}
              </p>
            </div>
          </CardTitle>
        </CardHeader>
        <CardContent className="pt-4">
          <div className="grid grid-cols-1 lg:grid-cols-2 gap-3 mb-6">
            <StatCard
              title="Deuda Pendiente"
              value={stats?.pendingDebt || 0}
              icon={AlertCircle}
              color="bg-red-600"
            />
          </div>

          {/* Annual Chart - By Payment Method */}
          <div className="h-64 sm:h-72">
            <p className="text-xs text-zinc-500 mb-2 text-center">Ingresos por Método de Pago</p>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart data={yearlyData} margin={{ top: 10, right: 10, left: 0, bottom: 0 }}>
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis 
                  dataKey="month" 
                  stroke="#52525B"
                  tick={{ fill: '#71717A', fontSize: 10 }}
                  interval={0}
                />
                <YAxis 
                  stroke="#52525B"
                  tick={{ fill: '#71717A', fontSize: 10 }}
                  tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}
                  width={45}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#0A0A0A', 
                    border: '1px solid #27272A',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: '#FAFAFA' }}
                  formatter={(value) => [formatCurrency(value), '']}
                  labelFormatter={(label, payload) => {
                    if (payload && payload[0]) {
                      const idx = payload[0].payload.monthIndex - 1;
                      return monthsFull[idx];
                    }
                    return label;
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                <Bar dataKey="chart_efectivo" name="Efectivo" fill="#10B981" radius={[2, 2, 0, 0]} />
                <Bar dataKey="chart_tarjeta" name="Tarjeta" fill="#3B82F6" radius={[2, 2, 0, 0]} />
                <Bar dataKey="chart_deuda" name="Deuda" fill="#EF4444" radius={[2, 2, 0, 0]} />
              </BarChart>
            </ResponsiveContainer>
          </div>

          {/* Annual Chart - By Category */}
          <div className="h-64 sm:h-72 mt-6">
            <p className="text-xs text-zinc-500 mb-2 text-center">Ingresos por Categoría</p>
            <ResponsiveContainer width="100%" height="100%">
              <BarChart 
                data={yearlyData.map(m => {
                  const catData = { ...m };
                  categoryList.forEach(cat => {
                    catData[`cat_${cat}`] = m.by_category?.[cat] || 0;
                  });
                  return catData;
                })} 
                margin={{ top: 10, right: 10, left: 0, bottom: 0 }}
              >
                <CartesianGrid strokeDasharray="3 3" stroke="#27272A" />
                <XAxis 
                  dataKey="month" 
                  stroke="#52525B"
                  tick={{ fill: '#71717A', fontSize: 10 }}
                  interval={0}
                />
                <YAxis 
                  stroke="#52525B"
                  tick={{ fill: '#71717A', fontSize: 10 }}
                  tickFormatter={(value) => value >= 1000 ? `${(value/1000).toFixed(0)}k` : value}
                  width={45}
                />
                <Tooltip 
                  contentStyle={{ 
                    backgroundColor: '#0A0A0A', 
                    border: '1px solid #27272A',
                    borderRadius: '4px',
                    fontSize: '12px'
                  }}
                  labelStyle={{ color: '#FAFAFA' }}
                  formatter={(value) => [formatCurrency(value), '']}
                  labelFormatter={(label, payload) => {
                    if (payload && payload[0]) {
                      const idx = payload[0].payload.monthIndex - 1;
                      return monthsFull[idx];
                    }
                    return label;
                  }}
                />
                <Legend wrapperStyle={{ fontSize: '12px' }} />
                {categoryList.map((cat) => {
                  return (
                    <Bar 
                      key={cat}
                      dataKey={`cat_${cat}`} 
                      name={getCategoryName(cat)} 
                      fill={getCategoryColor(cat)} 
                      radius={[2, 2, 0, 0]} 
                    />
                  );
                })}
              </BarChart>
            </ResponsiveContainer>
          </div>
        </CardContent>
      </Card>
      )}
    </div>
  );
};

export default Dashboard;
